import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, Drawer, IconButton, InputAdornment, MenuItem, TablePagination, TextField, Tooltip } from "@mui/material";
import { Close as CloseIcon, EmailOutlined as EmailIcon, PreviewOutlined as PreviewIcon, Refresh as RefreshIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  PortfolioGetEmailCommunication,
  PortfolioGetEmailCommunicationRecipient,
  PortfolioListEmailCommunications
} from "../../util/API/portfolionew";
import holtExchangeLogo from "../../images/holtexlogo.png";
import "./EmailHistory.scss";

const EVENTS = [
  ["unit_transfer.finalized", "Unit transfer finalized"],
  ["distribution.finalized", "Distribution finalized"],
  ["distribution.corrected", "Distribution corrected"],
  ["distribution.reversed", "Distribution reversed"],
  ["application.approved", "Application approved"],
  ["application.rejected", "Application rejected"],
  ["document.uploaded", "Document uploaded"],
  ["payout_instruction.approved", "Payout instruction approved"],
  ["payout_instruction.rejected", "Payout instruction rejected"]
];

const eventLabel = key => EVENTS.find(([value]) => value === key)?.[1] || String(key || "Email communication").replaceAll(".", " ");
const displayDate = value => value ? new Date(String(value).replace(" ", "T")).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";
const errorMessage = error => error?.response?.data?.message || error?.message || "Unable to load email history.";
const sourceLabel = item => item?.source_type === "fund_distribution"
  ? `DIST-${item.source_id}`
  : item?.source_type === "fund_unit_transfer"
    ? `TRF-${item.source_id}`
    : item?.source_id ? `${item.source_type || "Source"} #${item.source_id}` : "—";
const historyPreviewStyles = `<style>
  [style*="white-space:pre-wrap"],
  [style*="white-space: pre-wrap"] {
    white-space: pre-line !important;
  }

  [style*="white-space:pre-wrap"] p,
  [style*="white-space: pre-wrap"] p,
  [style*="white-space:pre-wrap"] div,
  [style*="white-space: pre-wrap"] div,
  [style*="white-space:pre-wrap"] li,
  [style*="white-space: pre-wrap"] li,
  [style*="white-space:pre-wrap"] blockquote,
  [style*="white-space: pre-wrap"] blockquote {
    white-space: pre-line !important;
  }
</style>`;

const previewDocument = recipient => {
  if (!recipient?.body_html_rendered) {
    return `<!doctype html><html><body><pre style="white-space:pre-wrap;font:15px/1.6 Arial,sans-serif">${String(recipient?.body_text_rendered || "No email body was recorded.").replaceAll("&", "&amp;").replaceAll("<", "&lt;")}</pre></body></html>`;
  }

  const document = recipient.body_html_rendered.replace(/cid:portfolio-logo/gi, holtExchangeLogo);
  return /<\/head>/i.test(document)
    ? document.replace(/<\/head>/i, `${historyPreviewStyles}</head>`)
    : `${historyPreviewStyles}${document}`;
};

function Status({ value }) {
  return <span className={`yp-email-history-status ${String(value || "unknown").toLowerCase()}`}>{String(value || "unknown").replaceAll("_", " ")}</span>;
}

function renderDetailDrawer(detail, loading, close, openPreview) {
  return <Drawer anchor="right" open={Boolean(detail)} onClose={() => close(null)} PaperProps={{ className: "yp-drawer yp-email-history-detail" }}>
    {detail && <div className="yp-email-history-drawer-body">
      <header><div><span className="yp-eyebrow">Communication batch</span><h2>{eventLabel(detail.communication?.event_key)}</h2><p>{sourceLabel(detail.communication)} · {detail.communication?.template_name || "Template unavailable"}</p></div><IconButton aria-label="Close recipient history" onClick={() => close(null)}><CloseIcon /></IconButton></header>
      <div className="yp-email-history-summary"><article><span>Recipients</span><strong>{Number(detail.communication?.recipient_count || 0)}</strong></article><article className="sent"><span>Sent</span><strong>{Number(detail.communication?.sent_count || 0)}</strong></article><article className="failed"><span>Failed</span><strong>{Number(detail.communication?.failed_count || 0)}</strong></article><article><span>Skipped</span><strong>{Number(detail.communication?.skipped_count || 0)}</strong></article></div>
      {loading ? <div className="yp-email-history-empty"><CircularProgress size={26} /><strong>Loading recipients…</strong></div> : <div className="yp-email-recipient-list">
        {(detail.recipients || []).map(recipient => <article key={recipient.id}>
          <div><strong>{recipient.recipient_name || "Unnamed recipient"}</strong><small>{recipient.recipient_email || "No email address"}</small></div>
          <div><Status value={recipient.status} /><small>{recipient.status === "sent" ? displayDate(recipient.sent_at) : recipient.failed_at ? displayDate(recipient.failed_at) : displayDate(recipient.created_at)}</small></div>
          <div><strong>{Number(recipient.attempt_count || 0)}</strong><small>attempt{Number(recipient.attempt_count) === 1 ? "" : "s"}</small></div>
          <Button startIcon={<PreviewIcon />} variant="outlined" onClick={() => openPreview(recipient)}>Preview</Button>
          {recipient.last_error && <Alert severity="error">{recipient.last_error}</Alert>}
        </article>)}
        {!detail.recipients?.length && <div className="yp-email-history-empty"><EmailIcon /><strong>No recipients recorded</strong></div>}
      </div>}
    </div>}
  </Drawer>;
}

function renderPreviewDrawer(preview, loading, close) {
  return <Drawer anchor="right" open={Boolean(preview)} onClose={() => close(null)} PaperProps={{ className: "yp-drawer yp-email-history-preview" }}>
    {preview && <div className="yp-email-history-drawer-body">
      <header><div><span className="yp-eyebrow">Delivered content</span><h2>Email preview</h2><p>This is the content recorded for this recipient.</p></div><IconButton aria-label="Close email preview" onClick={() => close(null)}><CloseIcon /></IconButton></header>
      {loading ? <div className="yp-email-history-empty"><CircularProgress size={26} /><strong>Loading email…</strong></div> : <>
        <div className="yp-email-preview-meta">
          <div className="yp-email-preview-meta-row"><span>To</span><div><strong>{preview.recipient_name}</strong><small>{preview.recipient_email}</small></div></div>
          <div className="yp-email-preview-meta-row"><span>Subject</span><div><strong>{preview.subject_rendered || "No subject"}</strong></div></div>
          <div className="yp-email-preview-meta-row"><span>Delivery</span><div><Status value={preview.status} /></div></div>
          {preview.provider_message_id && <div className="yp-email-preview-meta-row"><span>Provider ID</span><div><small>{preview.provider_message_id}</small></div></div>}
        </div>
        <iframe title={`Email sent to ${preview.recipient_name || preview.recipient_email}`} sandbox="" srcDoc={previewDocument(preview)} />
      </>}
    </div>}
  </Drawer>;
}

export default function EmailHistory({ funds = [] }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 10, total: 0 });
  const [filters, setFilters] = useState({ search: "", appliedSearch: "", status: "", event_key: "", fund_id: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotice("");
    try {
      const response = await PortfolioListEmailCommunications({
        page: pagination.page + 1,
        per_page: pagination.rowsPerPage,
        search: filters.appliedSearch || undefined,
        status: filters.status || undefined,
        event_key: filters.event_key || undefined,
        fund_id: filters.fund_id || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined
      });
      const data = response.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setPagination(current => ({ ...current, total: Number(data.pagination?.total || 0) }));
    } catch (error) { setItems([]); setNotice(errorMessage(error)); }
    finally { setLoading(false); }
  }, [filters.appliedSearch, filters.date_from, filters.date_to, filters.event_key, filters.fund_id, filters.status, pagination.page, pagination.rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const fundNames = useMemo(() => new Map(funds.map(fund => [String(fund.id), fund.name])), [funds]);
  const updateFilter = (name, value) => {
    setFilters(current => ({ ...current, [name]: value }));
    setPagination(current => ({ ...current, page: 0 }));
  };
  const applySearch = () => updateFilter("appliedSearch", filters.search.trim());

  const openDetail = async item => {
    setDetail({ communication: item, recipients: [] });
    setDetailLoading(true);
    try { setDetail((await PortfolioGetEmailCommunication(item.id)).data); }
    catch (error) { setNotice(errorMessage(error)); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const openPreview = async recipient => {
    setPreview({ ...recipient, loading: true });
    setPreviewLoading(true);
    try { setPreview((await PortfolioGetEmailCommunicationRecipient(recipient.communication_id, recipient.id)).data); }
    catch (error) { setNotice(errorMessage(error)); setPreview(null); }
    finally { setPreviewLoading(false); }
  };

  return <section className="yp-email-history">
    <div className="yp-email-history-heading"><div><span className="yp-eyebrow">Delivery audit</span><h2>Email history</h2><p>Review investor emails, delivery results, and the exact content prepared for each recipient.</p></div><Tooltip title="Refresh history"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip></div>
    {notice && <Alert severity="error" onClose={() => setNotice("")}>{notice}</Alert>}
    <form className="yp-email-history-filters" onSubmit={event => { event.preventDefault(); applySearch(); }}>
      <TextField size="small" placeholder="Search recipient, event, template, or source" value={filters.search} onChange={event => setFilters(current => ({ ...current, search: event.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      <TextField select size="small" label="Status" value={filters.status} onChange={event => updateFilter("status", event.target.value)}><MenuItem value="">All statuses</MenuItem>{[["sent", "Sent"], ["partially_sent", "Partially sent"], ["failed", "Failed"], ["processing", "Processing"]].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
      <TextField select size="small" label="Event" value={filters.event_key} onChange={event => updateFilter("event_key", event.target.value)}><MenuItem value="">All events</MenuItem>{EVENTS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
      <TextField select size="small" label="Fund" value={filters.fund_id} onChange={event => updateFilter("fund_id", event.target.value)}><MenuItem value="">All funds</MenuItem>{funds.map(fund => <MenuItem key={fund.id} value={String(fund.id)}>{fund.name}</MenuItem>)}</TextField>
      <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.date_from} onChange={event => updateFilter("date_from", event.target.value)} />
      <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.date_to} onChange={event => updateFilter("date_to", event.target.value)} />
      <Button type="submit" variant="outlined">Search</Button>
    </form>
    <div className="yp-email-history-table">
      <div className="yp-email-history-table-head"><span>Communication</span><span>Fund / source</span><span>Recipients</span><span>Delivery</span><span>Created</span><span>Action</span></div>
      {loading ? <div className="yp-email-history-empty"><CircularProgress size={26} /><strong>Loading email history…</strong></div> : items.map(item => <article key={item.id}>
        <div className="yp-email-history-event"><span><EmailIcon /></span><div><strong>{eventLabel(item.event_key)}</strong><small>{item.template_name || "Template unavailable"}{item.template_version_number ? ` · Version ${item.template_version_number}` : ""}</small></div></div>
        <div><strong>{fundNames.get(String(item.fund_id)) || "Portfolio"}</strong><small>{sourceLabel(item)}</small></div>
        <div><strong>{Number(item.recipient_count || 0)}</strong><small>recipient{Number(item.recipient_count) === 1 ? "" : "s"}</small></div>
        <div><Status value={item.status} /><small>{Number(item.sent_count || 0)} sent · {Number(item.failed_count || 0)} failed · {Number(item.skipped_count || 0)} skipped</small></div>
        <div><strong>{displayDate(item.created_at)}</strong><small>{item.completed_at ? `Completed ${displayDate(item.completed_at)}` : "Not completed"}</small></div>
        <Button variant="outlined" onClick={() => openDetail(item)}>View recipients</Button>
      </article>)}
      {!loading && !items.length && <div className="yp-email-history-empty"><EmailIcon /><strong>No email history found</strong><span>Sent and attempted communications will appear here.</span></div>}
      <TablePagination component="div" count={pagination.total} page={pagination.page} rowsPerPage={pagination.rowsPerPage} rowsPerPageOptions={[10, 20, 50, 100]} onPageChange={(_, page) => setPagination(current => ({ ...current, page }))} onRowsPerPageChange={event => setPagination(current => ({ ...current, page: 0, rowsPerPage: Number(event.target.value) }))} />
    </div>
    {renderDetailDrawer(detail, detailLoading, setDetail, openPreview)}
    {renderPreviewDrawer(preview, previewLoading, setPreview)}
  </section>;
}
